import { motion, AnimatePresence } from "motion/react";
import {
    Bell,
    X,
    AlertTriangle,
    CreditCard,
    TrendingUp,
    Shield,
    Check,
    Trash2
} from "lucide-react";

import { useRWAStore, Notification } from "../store/rwa-store";

interface NotificationsPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
    const { notifications, markNotificationRead, markAllNotificationsRead, clearNotifications } = useRWAStore();

    const unreadCount = notifications.filter(n => !n.read).length;

    const getIcon = (type: Notification['type']) => {
        switch (type) {
            case 'kyc': return Shield;
            case 'transaction': return CreditCard;
            case 'price-alert': return TrendingUp;
            case 'dividend': return TrendingUp;
            case 'security': return AlertTriangle;
            default: return Bell;
        }
    };

    const getIconColor = (type: Notification['type']) => {
        switch (type) {
            case 'kyc': return 'bg-purple-500/10 text-purple-500';
            case 'transaction': return 'bg-blue-500/10 text-blue-500';
            case 'price-alert': return 'bg-amber-500/10 text-amber-500';
            case 'dividend': return 'bg-emerald-500/10 text-emerald-500';
            case 'security': return 'bg-red-500/10 text-red-500';
            default: return 'bg-neutral-500/10 text-neutral-500';
        }
    };

    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40"
                        onClick={onClose}
                    />

                    {/* Panel */}
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute top-full right-0 mt-2 w-96 bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl shadow-black/50 z-50 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-neutral-800 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h3 className="font-bold">Notifications</h3>
                                {unreadCount > 0 && (
                                    <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-500 text-xs font-medium rounded-full">
                                        {unreadCount} new
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={() => markAllNotificationsRead()}
                                        className="p-2 hover:bg-neutral-800 rounded-lg text-neutral-400 hover:text-white transition-colors"
                                        title="Mark all as read"
                                    >
                                        <Check className="w-4 h-4" />
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-neutral-800 rounded-lg text-neutral-400 hover:text-white transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Notifications List */}
                        <div className="max-h-96 overflow-y-auto">
                            {notifications.length > 0 ? (
                                <div className="divide-y divide-neutral-800/50">
                                    {notifications.map(notification => {
                                        const Icon = getIcon(notification.type);
                                        const iconColor = getIconColor(notification.type);

                                        return (
                                            <motion.div
                                                key={notification.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className={`p-4 hover:bg-neutral-800/50 transition-colors cursor-pointer ${!notification.read ? 'bg-neutral-800/30' : ''
                                                    }`}
                                                onClick={() => markNotificationRead(notification.id)}
                                            >
                                                <div className="flex gap-3">
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconColor}`}>
                                                        <Icon className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="font-medium text-sm">{notification.title}</div>
                                                            {!notification.read && (
                                                                <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 mt-1.5" />
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-neutral-400 line-clamp-2 mt-0.5">
                                                            {notification.message}
                                                        </p>
                                                        <div className="text-xs text-neutral-500 mt-1">
                                                            {formatTimestamp(notification.timestamp)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="p-8 text-center text-neutral-500">
                                    <Bell className="w-8 h-8 mx-auto mb-3 opacity-50" />
                                    <p className="text-sm">No notifications yet</p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {notifications.length > 0 && (
                            <div className="p-3 border-t border-neutral-800">
                                <button
                                    onClick={() => clearNotifications()}
                                    className="w-full py-2 text-sm text-neutral-400 hover:text-red-500 flex items-center justify-center gap-2 transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Clear all notifications
                                </button>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

// Bell Button with Badge (exported for use in header)
export function NotificationBell({ onClick, unreadCount }: { onClick: () => void; unreadCount: number }) {
    return (
        <button
            onClick={onClick}
            className="relative p-2 hover:bg-neutral-800 rounded-lg transition-colors"
        >
            <Bell className="w-5 h-5 text-neutral-400" />
            {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                </span>
            )}
        </button>
    );
}
